import React, { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Alert, Button, ButtonGroup, Card, CardBody, Col, Row } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAward, faCoins, faFire, faLayerGroup } from '@fortawesome/free-solid-svg-icons'
import PageHeader from '../../common/PageHeader'
import Loader from '../../common/Loader'
import Flex from '../../common/Flex'
import useSWRFetch from '../../../helpers/hooks/useSWRFetch'
import { getCondominiumRewardsSummary } from '../../../helpers/api/rewards'
import { formatDate } from '../../rewards/rewardsUtils'
import PunctualityImpact from './PunctualityImpact'
import AdoptionStats from './AdoptionStats'
import ProgramShareCard from './ProgramShareCard'

const MONTH_OPTIONS = [6, 12]

// Reglas por defecto (RewardRulesSeeder) si la respuesta no las trae.
const DEFAULT_DEADLINE_DAY = 5
const DEFAULT_HC_PER_USD = 100

const HOW_IT_WORKS = [
  { icon: faCoins, title: 'Ganan por pagar', text: 'Cada pago de cuota aprobado da puntos (XP) y Homelan Coins.' },
  { icon: faFire, title: 'Premia la puntualidad', text: 'Pagar antes del día límite da un bono, y los meses seguidos a tiempo multiplican las Coins.' },
  { icon: faLayerGroup, title: 'Suben de nivel', text: 'Con el XP pasan de Bronce a Plata y Oro, y cada nivel gana más Coins por pago.' },
  { icon: faAward, title: 'Abonan a su cuota', text: 'Canjean sus Coins por un abono en dólares a su cuenta del condominio.' }
]

// Estado vacío: el condominio todavía no tiene propietarios en el programa.
const ProgramExplainer = () => (
  <Card className="mb-3">
    <CardBody>
      <h5 className="mb-1">Tu condominio aún no tiene propietarios en Homelan Rewards</h5>
      <p className="fs--1 text-600 mb-3">
        Homelan Rewards premia a los propietarios que pagan su cuota a tiempo. Apenas se apruebe el primer pago de un
        propietario del condominio, aquí verás cuántos participan y cómo cambia la puntualidad.
      </p>
      <Row>
        {HOW_IT_WORKS.map((item) => (
          <Col sm={6} lg={3} key={item.title} className="mb-3">
            <Flex align="start">
              <FontAwesomeIcon icon={item.icon} className="text-primary mr-2 mt-1" />
              <div>
                <h6 className="mb-1">{item.title}</h6>
                <p className="fs--1 text-600 mb-0">{item.text}</p>
              </div>
            </Flex>
          </Col>
        ))}
      </Row>
    </CardBody>
  </Card>
)

// HO-TASK-288: adopción de Homelan Rewards en el condominio seleccionado y su
// impacto en la puntualidad, para que el administrador lo lleve a la junta.
const CondominiumRewards = () => {
  const { selectedCondominium } = useSelector((state) => state.condominium)
  const [months, setMonths] = useState(MONTH_OPTIONS[0])
  const { data, error, isLoading, mutate } = useSWRFetch(
    selectedCondominium ? ['rewards-condominium-summary', selectedCondominium, months] : null,
    (key, condominiumId, range) => getCondominiumRewardsSummary(condominiumId, range)
  )
  // Al cambiar el rango se mantiene el resumen anterior (atenuado) mientras
  // llega el nuevo, en vez de volver a mostrar el loader. Solo si es del mismo
  // condominio: al cambiar de condominio no se muestran datos de otro.
  const lastSummary = useRef(null)
  if (data?.data) lastSummary.current = { condominium: selectedCondominium, summary: data.data }
  const refreshing = isLoading && lastSummary.current?.condominium === selectedCondominium
  const summary = data?.data || (refreshing ? lastSummary.current.summary : null)

  const header = (
    <PageHeader
      title="Homelan Rewards en tu condominio"
      titleTag="h2"
      description="Cuántos propietarios participan en el programa y cómo cambió la puntualidad de los pagos desde que arrancó."
      className="mb-3"
      col={{ lg: 9 }}
    />
  )

  if (!selectedCondominium) {
    return (
      <>
        {header}
        <Alert color="info">
          Elige un condominio en <Link to="/dashboard/condominiums">Condominios</Link> para ver su resumen de Homelan
          Rewards.
        </Alert>
      </>
    )
  }

  const renderContent = () => {
    if (isLoading && !refreshing) {
      return (
        <Card className="mb-3">
          <Loader padding="py-5" />
        </Card>
      )
    }

    if (error || !summary) {
      return (
        <Alert color="danger" className="mb-3">
          No pudimos cargar el resumen de Homelan Rewards del condominio.
          <Button color="link" size="sm" className="ml-1 p-0 align-baseline" onClick={() => mutate()}>
            Reintentar
          </Button>
        </Alert>
      )
    }

    const deadlineDay = Number(summary.program?.deadline_day) || DEFAULT_DEADLINE_DAY
    const hcPerUsd = Number(summary.program?.hc_per_usd) || DEFAULT_HC_PER_USD
    const shareCard = (
      <ProgramShareCard condominiumName={summary.condominium?.name} deadlineDay={deadlineDay} hcPerUsd={hcPerUsd} />
    )

    if (!summary.program?.started_at || !Number(summary.participants)) {
      return (
        <>
          <ProgramExplainer />
          {shareCard}
        </>
      )
    }

    return (
      <>
        <Flex justify="between" align="center" className="mb-3 flex-wrap">
          <span className="fs--1 text-600 mb-2 mb-sm-0">
            Programa activo desde el <b className="text-900">{formatDate(summary.program.started_at)}</b>
          </span>
          <Flex align="center">
            <span className="fs--1 text-600 mr-2">Comparar</span>
            <ButtonGroup size="sm" aria-label="Meses a comparar antes y después del programa">
              {MONTH_OPTIONS.map((option) => (
                <Button
                  key={option}
                  color={option === months ? 'primary' : 'falcon-default'}
                  onClick={() => setMonths(option)}
                  aria-pressed={option === months}
                >
                  {option} meses
                </Button>
              ))}
            </ButtonGroup>
          </Flex>
        </Flex>
        <div style={{ opacity: refreshing ? 0.5 : 1, transition: 'opacity 150ms' }} aria-busy={refreshing}>
          <PunctualityImpact punctuality={summary.punctuality} deadlineDay={deadlineDay} />
          <AdoptionStats summary={summary} />
        </div>
        {shareCard}
      </>
    )
  }

  return (
    <>
      {header}
      {renderContent()}
    </>
  )
}

export default CondominiumRewards
